// src/components/Login.jsx

import React, { useEffect, useMemo, useRef, useState } from "react";

import { Link, useLocation, useNavigate } from "react-router-dom";

import "../styles/login.css";

import logo from "../assets/PetfamLogo.png";

import perroGato from "../assets/perro_gato.webp";

import useBackend from "../hooks/useBackend";

import { Eye, EyeOff, X } from "lucide-react";

import config from "../config";

import { authFetch } from "../utils/authFetch";

// ✅ Assets para modal pago (igual que Interfaz)

import chipImg from "../assets/chip.webp";

import visa from "../assets/visa.webp";

import mc from "../assets/mastercard.webp";

import amex from "../assets/amex.webp";

export default function Login() {

  const location = useLocation();

  const navigate = useNavigate();

  const backTo =

    location.state?.from === "signup"

      ? "/crear-cuenta"

      : location.state?.from === "servicios"

      ? "/servicios"

      : "/";

  const { post } = useBackend();

  const [loginLoading, setLoginLoading] = useState(false);

  const [forgotLoading, setForgotLoading] = useState(false);

  // ✅ NUEVO: mientras se revisa si hay una sesión guardada en el dispositivo.
  // Arranca en true solo si hay un token guardado, para que el formulario no
  // "parpadee" habilitado antes de redirigir.
  const [sessionChecking, setSessionChecking] = useState(() => !!localStorage.getItem("token"));

  // ✅ NUEVO: de dónde vino la última respuesta de autenticación
  // ("login" = formulario, "session" = sesión guardada). Se usa para saber
  // cómo reintentar la entrada después de pagar desde el modal.
  const authSourceRef = useRef("login");

  const [formData, setFormData] = useState({ email: "", password: "" });

  const [showPass, setShowPass] = useState(false);

  const [fieldErrors, setFieldErrors] = useState({});

  const [loginError, setLoginError] = useState("");

  const [forgotMessage, setForgotMessage] = useState("");

  const [forgotInlineError, setForgotInlineError] = useState("");

  const API_BASE_URL = String(config.apiBaseUrl || "").replace(/\/+$/, "");

  const apiUrl = (path) =>

    String(path || "").startsWith("http") ? path : `${API_BASE_URL}${path}`;

  // =========================

  // ✅ MODAL CUENTA SUSPENDIDA

  // =========================

  const [openSuspendedModal, setOpenSuspendedModal] = useState(false);

  const [suspendedMsg, setSuspendedMsg] = useState("");

  const [suspendedUser, setSuspendedUser] = useState(null); // {idUser,email,name}

  // =========================

  // ✅ MODAL PAGO (desde login)

  // =========================

  const [openPaymentModal, setOpenPaymentModal] = useState(false);

  const [payLoading, setPayLoading] = useState(false);

  const [payError, setPayError] = useState("");

  const [paySuccess, setPaySuccess] = useState(false);

  const [cardType, setCardType] = useState("visa"); // "visa" | "mc" | "amex"

  const [cardHolder, setCardHolder] = useState("");

  const [cardNumber, setCardNumber] = useState("");

  const [cardExpMonth, setCardExpMonth] = useState("");

  const [cardExpYear, setCardExpYear] = useState("");

  const [cardCvc, setCardCvc] = useState("");

  const IS_QA = useMemo(() => {

    const host = (window?.location?.hostname || "").toLowerCase();

    const api = String(API_BASE_URL || "").toLowerCase();

    return host.includes("qa") || api.includes("qa");

  }, [API_BASE_URL]);

  const wompiTokenizeEndpoint = () => {

    return IS_QA

      ? "https://sandbox.wompi.co/v1/tokens/cards"

      : "https://production.wompi.co/v1/tokens/cards";

  };

  const resolveUserId = (u) => {

    const id = u?.idUser ?? u?.id ?? u?.userId ?? null;

    return id != null ? Number(id) : null;

  };

  const userHasPets = async (idUser) => {

    try {

      const res = await authFetch(apiUrl(`/pets/user/${idUser}`));

      const json = await res.json().catch(() => null);

      const list = json?.data;

      return Array.isArray(list) && list.length > 0;

    } catch {

      return true;

    }

  };

  const handleChange = (e) => {

    const { name, value } = e.target;

    setFormData((prev) => ({ ...prev, [name]: value }));

    setFieldErrors((prev) => ({ ...prev, [name]: "" }));

    setLoginError("");

    setForgotMessage("");

    setForgotInlineError("");

  };

  const validate = () => {

    const errs = {};

    if (!formData.email.trim()) {

      errs.email = "El correo es obligatorio.";

    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email.trim())) {

      errs.email = "Correo inválido. Usa el formato correo@ejemplo.com.";

    }

    if (!formData.password) {

      errs.password = "La contraseña es obligatoria.";

    }

    setFieldErrors(errs);

    return Object.keys(errs).length === 0;

  };

  const hardClearSession = () => {

    localStorage.removeItem("token");

    localStorage.removeItem("idUser");

    localStorage.removeItem("petfamUser");

    localStorage.removeItem("userRole");

    localStorage.removeItem("userRoles");

    localStorage.removeItem("userEmail");

  };

  // =========================

  // Helpers pago

  // =========================

  const onlyDigits = (s) => String(s || "").replace(/\D/g, "");

  const formatCardNumber = (value) => {

    const d = onlyDigits(value).slice(0, 16);

    return d.replace(/(\d{4})(?=\d)/g, "$1 ").trim();

  };

  function normalizeCardHolder(value) {

    return String(value || "")

      .replace(/[^A-Za-zÁÉÍÓÚÜÑáéíóúüñ\s]/g, "")

      .toUpperCase()

      .replace(/\s+/g, " ")

      .trimStart();

  }

  const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

  async function pollTxFinal(txId) {

    const maxTries = 18; // ~36s

    for (let i = 0; i < maxTries; i++) {

      await sleep(2000);

      const res = await fetch(apiUrl(`/wompi/transactions/status/${txId}`), {

        cache: "no-store",

      });

      const json = await res.json().catch(() => null);

      if (json?.ok) {

        const st = String(json.status || "").toUpperCase();

        if (st === "APPROVED") return "APPROVED";

        if (st === "DECLINED" || st === "ERROR" || st === "VOIDED") return st;

      }

    }

    return "PENDING";

  }

  // =========================================================
  // ✅ Procesa la respuesta de /auth/login o /auth/session con las
  // MISMAS reglas (antes esto vivía completo dentro de runLoginFlow).
  //
  // isSession = true cuando viene de la revisión automática de la sesión
  // guardada. Diferencias en ese caso:
  // - No se muestran mensajes de error en rojo (la persona no escribió nada).
  // - Si la cuenta tiene restricción (423) NO se borra el token, para poder
  //   reintentar la entrada automática después de pagar desde el modal.
  // Para el login con formulario (isSession = false) todo funciona igual
  // que antes.
  // =========================================================
  const handleAuthResponse = async (resp, isSession) => {

    if (!resp) {

      if (!isSession) {

        setLoginError(

          "Ocurrió un error al conectarse con el servidor. Inténtalo de nuevo."

        );

      }

      return;

    }

    const { status, data, message, code, userMessage } = resp;

    // ✅ CUENTA SUSPENDIDA -> MODAL

    if (status === 423 || code === "ACCOUNT_SUSPENDED") {

      if (!isSession) hardClearSession();

      const minimal = {

        idUser: data?.idUser ?? data?.id ?? data?.userId ?? null,

        email: (data?.email || formData.email || "")

          .toString()

          .trim()

          .toLowerCase(),

        name: data?.name ?? "",

        country: (data?.country || "").toString().trim().toUpperCase(),

        accessReason: (data?.accessReason || "").toString().trim().toUpperCase(),

      };

      setSuspendedUser(minimal);

      const isTrialExpired = minimal.accessReason === "TRIAL_EXPIRED";

      setSuspendedMsg(

        userMessage ||

          message ||

          (isTrialExpired

            ? "Tus 30 días gratis han terminado. Toda tu información sigue guardada. Activa tu membresía para continuar usando Petfam."

            : "Tu cuenta está suspendida por un pago pendiente. Actualiza tu método de pago para reactivarla.")

      );

      setOpenSuspendedModal(true);

      return;

    }

    if (status === 401 || status === 403) {

      if (!isSession) setLoginError("El correo o la contraseña no están correctos.");

      hardClearSession();

      return;

    }

    if (status !== 200 || !data) {

      if (!isSession) {

        setLoginError(

          message ||

            "Ocurrió un error al conectarse con el servidor. Inténtalo de nuevo."

        );

      }

      return;

    }

    const newId = data?.idUser || data?.id || data?.userId;

    if (!newId) {

      if (!isSession) {

        setLoginError(

          "No se recibió idUser del backend. Revisa la respuesta de /auth/login."

        );

      }

      hardClearSession();

      return;

    }

    const tokenFromBackend =

      resp.token || data.token || data.jwt || data.accessToken || data.idToken || null;

    const finalToken = tokenFromBackend || `user-${newId}`;

    localStorage.setItem("token", finalToken);

    const rawRoles = Array.isArray(data.roles) ? data.roles : [];

    localStorage.setItem("userRoles", JSON.stringify(rawRoles));

    let mainRole = null;

    if (rawRoles.includes("ROLE_ADMIN")) mainRole = "ADMIN";

    else if (rawRoles.includes("ROLE_CLIENTE")) mainRole = "CLIENTE";

    if (mainRole) localStorage.setItem("userRole", mainRole);

    localStorage.setItem("petfamUser", JSON.stringify(data));

    localStorage.setItem("idUser", String(newId));

    const resolvedEmail = data?.email || formData.email.trim();

    if (resolvedEmail) localStorage.setItem("userEmail", resolvedEmail);

    if (mainRole === "ADMIN") {

      navigate("/admin", { state: { from: "login", transition: "up" }, replace: true });

      return;

    }

    try {

      const idUser = resolveUserId(data) || Number(newId);

      const redirectTo =

        (typeof location.state?.fromPath === "string" && location.state.fromPath) ||

        "/interfaz";

      const hasPets = await userHasPets(idUser);

      if (!hasPets) {

        navigate("/welcomepet", { state: { from: "login", transition: "up" }, replace: true });

        return;

      }

      navigate(redirectTo, { state: { from: "login", transition: "up" }, replace: true });

    } catch {

      navigate("/interfaz", { state: { from: "login", transition: "up" }, replace: true });

    }

  };

  const runLoginFlow = async () => {

    authSourceRef.current = "login";

    const resp = await post("/auth/login", "", {

      email: formData.email.trim(),

      password: formData.password,

    });

    await handleAuthResponse(resp, false);

  };

  // ✅ NUEVO: entra con la sesión guardada en el dispositivo (sin contraseña).
  // El backend valida el token y aplica las mismas reglas del login.
  const runSessionFlow = async () => {

    const savedToken = localStorage.getItem("token");

    if (!savedToken) return;

    authSourceRef.current = "session";

    const resp = await post("/auth/session", savedToken, {});

    await handleAuthResponse(resp, true);

  };

  // ✅ NUEVO: al abrir el login, si hay un token guardado, intenta entrar
  // directo. Si el backend no responde (sin internet, servidor caído) NO se
  // borra la sesión: simplemente se muestra el formulario.
  useEffect(() => {

    if (!localStorage.getItem("token")) {

      setSessionChecking(false);

      return;

    }

    let cancelled = false;

    (async () => {

      try {

        await runSessionFlow();

      } catch {

        // Error de red o del servidor: se deja el formulario disponible.

      } finally {

        if (!cancelled) setSessionChecking(false);

      }

    })();

    return () => {

      cancelled = true;

    };

    // eslint-disable-next-line react-hooks/exhaustive-deps

  }, []);

  const handleLogin = async (e) => {

    e.preventDefault();

    setLoginError("");

    setForgotMessage("");

    setForgotInlineError("");

    if (!validate()) return;

    try {

      setLoginLoading(true);

      await runLoginFlow();

    } catch {

      setLoginError(

        "Ocurrió un error al conectarse con el servidor. Inténtalo de nuevo."

      );

    } finally {

      setLoginLoading(false);

    }

  };

  const handleForgotPassword = async (e) => {

    e.preventDefault();

    setForgotMessage("");

    setLoginError("");

    setForgotInlineError("");

    if (!formData.email.trim()) {

      setForgotInlineError("Debes poner tu correo");

      return;

    }

    try {

      setForgotLoading(true);

      await post("/auth/forgot-password", "", { email: formData.email.trim() });

      setForgotMessage("Te hemos enviado un correo para recuperar tu contraseña.");

    } catch {

      setForgotInlineError(

        "No fue posible enviar el correo de recuperación. Inténtalo de nuevo."

      );

    } finally {

      setForgotLoading(false);

    }

  };

  const anyLoading = loginLoading || forgotLoading || payLoading || sessionChecking;

  // ===========================================

  // ✅ UN SOLO BOTÓN: Guardar y cobrar

  // ===========================================

  const handleSaveAndChargeFromLogin = async () => {

    try {

      setPayLoading(true);

      setPayError("");

      setPaySuccess(false);

      const email = (suspendedUser?.email || formData.email || "")

        .toString()

        .trim()

        .toLowerCase();

      const idUser = resolveUserId(suspendedUser) || suspendedUser?.idUser;

      if (!email) throw new Error("No encontré el email del usuario.");

      if (!idUser) throw new Error("No se pudo identificar el usuario (idUser).");

      // Validación mínima campos

      const num = onlyDigits(cardNumber);

      if (!String(cardHolder || "").trim()) throw new Error("El nombre del titular es obligatorio.");

      if (num.length < 13) throw new Error("Número de tarjeta inválido.");

      if (onlyDigits(cardCvc).length < 3) throw new Error("CVC inválido.");

      const mm = Number(onlyDigits(cardExpMonth));

      if (!mm || mm < 1 || mm > 12) throw new Error("Mes de expiración inválido.");

      if (onlyDigits(cardExpYear).length < 2) throw new Error("Año de expiración inválido.");

      // 1) merchant (publicKey + tokens)

      const mRes = await fetch(apiUrl("/wompi/merchant"), { cache: "no-store" });

      const merchant = await mRes.json().catch(() => null);

      if (!mRes.ok || !merchant?.publicKey) {

        throw new Error(merchant?.message || "No se pudo obtener merchant de Wompi.");

      }

      const publicKey = merchant.publicKey;

      const acceptanceToken = merchant?.acceptance?.token;

      const personalAuthToken = merchant?.personalData?.token;

      if (!acceptanceToken || !personalAuthToken) {

        throw new Error("Wompi merchant no devolvió tokens de aceptación.");

      }

      // 2) Tokenizar tarjeta (tok_...)

      const payloadToken = {

        number: onlyDigits(cardNumber),

        cvc: onlyDigits(cardCvc),

        exp_month: onlyDigits(cardExpMonth),

        exp_year: onlyDigits(cardExpYear),

        card_holder: String(cardHolder || "").trim(),

      };

      const tRes = await fetch(wompiTokenizeEndpoint(), {

        method: "POST",

        headers: {

          "Content-Type": "application/json",

          Authorization: `Bearer ${publicKey}`,

        },

        body: JSON.stringify(payloadToken),

      });

      const tJson = await tRes.json().catch(() => null);

      const token = tJson?.data?.id;

      if (!tRes.ok || !token) {

        const msg = tJson?.error?.messages

          ? JSON.stringify(tJson.error.messages)

          : tJson?.error?.reason || tJson?.message || "Wompi rechazó la tokenización.";

        throw new Error(msg);

      }

      // 3) Crear Payment Source en backend

      const psRes = await fetch(apiUrl("/wompi/payment-sources/card"), {

        method: "POST",

        headers: { "Content-Type": "application/json" },

        body: JSON.stringify({

          token,

          customerEmail: email,

          acceptanceToken,

          acceptPersonalAuthToken: personalAuthToken,

        }),

      });

      const psJson = await psRes.json().catch(() => null);

      if (!psRes.ok || !psJson?.ok) {

        throw new Error(psJson?.message || "No se pudo crear la fuente de pago.");

      }

      const paymentSourceId = psJson?.data?.paymentSourceId;

      if (!paymentSourceId) throw new Error("Wompi no devolvió paymentSourceId.");

      // 5) Cobrar de una (sin discountCode)

      const cRes = await fetch(apiUrl("/wompi/transactions/charge"), {

        method: "POST",

        headers: { "Content-Type": "application/json" },

        body: JSON.stringify({

          paymentSourceId: Number(paymentSourceId),

          customerEmail: email,

          discountCode: null,

          installments: 1,

        }),

      });

      const cJson = await cRes.json().catch(() => null);

      if (!cRes.ok || !cJson?.ok) {

        throw new Error(cJson?.message || "No pude iniciar el cobro.");

      }

      const txId = cJson?.transactionId;

      if (!txId) throw new Error("No recibí transactionId.");

      // 6) Esperar estado final

      const finalStatus = await pollTxFinal(txId);

      if (finalStatus !== "APPROVED") {

        throw new Error(`Pago ${finalStatus}. Intenta con otra tarjeta.`);

      }

      // ✅ éxito visual

      setPaySuccess(true);

      // limpiar campos sensibles

      setCardHolder("");

      setCardNumber("");

      setCardExpMonth("");

      setCardExpYear("");

      setCardCvc("");

      // cerrar modal pago y modal suspendido

      setTimeout(() => {

        setOpenPaymentModal(false);

        setOpenSuspendedModal(false);

      }, 300);

      // reintentar la entrada: con la sesión guardada si el modal vino de
      // ahí (no hay correo/contraseña escritos), o con el formulario si no.

      setTimeout(async () => {

        try {

          setLoginLoading(true);

          if (authSourceRef.current === "session") {

            await runSessionFlow();

          } else {

            await runLoginFlow();

          }

        } finally {

          setLoginLoading(false);

        }

      }, 500);

    } catch (e) {

      setPayError(e?.message || "No se pudo reactivar con el pago.");

    } finally {

      setPayLoading(false);

      setTimeout(() => setPaySuccess(false), 1500);

    }

  };

  // ✅ Mientras se revisa la sesión guardada NO se muestra el formulario ni
  // ningún texto: solo el fondo crema, para que la persona pase directo a su
  // perfil sin ver el login. Si la sesión no sirve, aparece el login normal.
  if (sessionChecking) {

    return <div aria-busy="true" style={{ minHeight: "100vh", background: "#fff4e1" }} />;

  }

  return (

    <>

      <div className="route-wipe" aria-hidden="true" />

      <div className="login-shell" style={{ backgroundImage: `url(${perroGato})` }}>

        <Link to="/" className="login-back" aria-label="Volver al inicio">

          <svg

            xmlns="http://www.w3.org/2000/svg"

            width="28"

            height="28"

            fill="none"

            stroke="currentColor"

            strokeWidth="2"

            viewBox="0 0 24 24"

          >

            <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />

          </svg>

        </Link>

        <div className="login-card">

          <h2>Iniciar sesión</h2>

          <p className="login-required">

            Los campos con * son <span className="obligatorios">obligatorios</span>

          </p>

          {loginError && <p className="login-error">{loginError}</p>}

          {forgotMessage && <p className="login-info-message">{forgotMessage}</p>}

          <form onSubmit={handleLogin} className="login-form">

            <label>

              <span>Correo electrónico*</span>

              <input

                type="email"

                name="email"

                placeholder="correo@ejemplo.com"

                value={formData.email}

                onChange={handleChange}

                disabled={anyLoading}

                required

                autoComplete="email"

                inputMode="email"

                autoCapitalize="none"

                autoCorrect="off"

                spellCheck={false}

              />

              {fieldErrors.email && <p className="login-error-field">{fieldErrors.email}</p>}

            </label>

            <label>

              <span>Contraseña*</span>

              <div className="pw-field">

                <input

                  className="pw-input"

                  type={showPass ? "text" : "password"}

                  name="password"

                  placeholder="••••••••"

                  value={formData.password}

                  onChange={handleChange}

                  disabled={anyLoading}

                  required

                  autoComplete="current-password"

                  autoCapitalize="none"

                  autoCorrect="off"

                  spellCheck={false}

                />

                <button

                  type="button"

                  className="pw-toggle"

                  onClick={() => setShowPass((s) => !s)}

                  aria-label={showPass ? "Ocultar contraseña" : "Ver contraseña"}

                  title={showPass ? "Ocultar" : "Ver"}

                  disabled={anyLoading}

                >

                  {showPass ? <EyeOff size={18} /> : <Eye size={18} />}

                </button>

              </div>

              {fieldErrors.password && <p className="login-error-field">{fieldErrors.password}</p>}

            </label>

            <button

              type="button"

              className="forgot-link"

              onClick={handleForgotPassword}

              disabled={anyLoading}

            >

              {forgotLoading ? "Enviando correo..." : "Olvidé mi contraseña"}

            </button>

            {forgotInlineError && <p className="forgot-inline-error">{forgotInlineError}</p>}

            <button

              type="submit"

              className="pill primary login-submit"

              disabled={loginLoading || payLoading || sessionChecking}

            >

              {loginLoading ? "Iniciando sesión..." : "Iniciar sesión"}

            </button>

          </form>

          <div className="login-footer">

            <img src={logo} alt="Petfam logo" />

            <span className="welcome-banner">¡Bienvenido a Petfam!</span>

          </div>

        </div>

      </div>

      {/* =========================

          ✅ MODAL CUENTA SUSPENDIDA

         ========================= */}

      {openSuspendedModal && (

        <div

          style={{

            position: "fixed",

            inset: 0,

            background: "rgba(0,0,0,.38)",

            display: "flex",

            alignItems: "center",

            justifyContent: "center",

            zIndex: 9999,

            padding: 16,

          }}

          onClick={() => !anyLoading && setOpenSuspendedModal(false)}

        >

          <div

            style={{

              width: "min(420px, 100%)",

              background: "#fff8f0",

              borderRadius: 18,

              padding: 16,

              boxShadow: "0 18px 40px rgba(0,0,0,.18)",

              border: "1px solid rgba(0,0,0,.06)",

            }}

            onClick={(e) => e.stopPropagation()}

          >

            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10 }}>

              <h4 style={{ margin: 0, color: "#0b2f3e" }}>
                {suspendedUser?.accessReason === "TRIAL_EXPIRED"
                  ? "Tus 30 días gratis han terminado"
                  : "Cuenta suspendida"}
              </h4>

              <button

                type="button"

                onClick={() => !anyLoading && setOpenSuspendedModal(false)}

                style={{

                  width: 36,

                  height: 36,

                  borderRadius: 12,

                  border: "1px solid rgba(0,0,0,.08)",

                  background: "white",

                  cursor: "pointer",

                  display: "grid",

                  placeItems: "center",

                }}

                aria-label="Cerrar"

              >

                <X size={18} />

              </button>

            </div>

            <p style={{ marginTop: 10, marginBottom: 14, fontSize: 13, opacity: 0.8, lineHeight: 1.35 }}>

              {suspendedMsg ||

                "Tu cuenta está suspendida por un pago pendiente. Actualiza tu método de pago para reactivarla."}

            </p>

            <button

              type="button"

              onClick={() => {

                setPayError("");

                setPaySuccess(false);

                if (suspendedUser?.accessReason === "TRIAL_EXPIRED" && suspendedUser?.country === "EC") {

                  navigate("/pago-epayco", {

                    state: {

                      from: "trial-expired",

                      transition: "up",

                      email: suspendedUser?.email || formData.email,

                      backendUser: suspendedUser,

                      countryCode: "EC",

                      paymentProvider: "EPAYCO",

                      currency: "USD",

                    },

                  });

                  return;

                }

                setOpenPaymentModal(true);

              }}

              disabled={anyLoading}

              style={{

                width: "100%",

                padding: "10px 12px",

                borderRadius: 14,

                border: "none",

                background: "#0095A9",

                cursor: "pointer",

                fontWeight: 800,

                color: "white",

              }}

            >

              {suspendedUser?.accessReason === "TRIAL_EXPIRED"
                ? "Continuar con Petfam"
                : "Actualizar método de pago"}

            </button>

            <p style={{ marginTop: 10, fontSize: 12, opacity: 0.7 }}>

              Correo: <strong>{(suspendedUser?.email || formData.email || "—").toString()}</strong>

            </p>

          </div>

        </div>

      )}

      {/* =========================

          ✅ MODAL GUARDAR Y COBRAR (1 botón)

         ========================= */}

      {openPaymentModal && (

        <div

          style={{

            position: "fixed",

            inset: 0,

            background: "rgba(0,0,0,.38)",

            display: "flex",

            alignItems: "center",

            justifyContent: "center",

            zIndex: 10000,

            padding: 16,

          }}

          onClick={(e) => {

            if (e.target === e.currentTarget && !payLoading) setOpenPaymentModal(false);

          }}

        >

          <div

            style={{

              width: "min(520px, 100%)",

              background: "#fff8f0",

              borderRadius: 18,

              padding: 16,

              boxShadow: "0 18px 40px rgba(0,0,0,.18)",

              border: "1px solid rgba(0,0,0,.06)",

            }}

            onClick={(e) => e.stopPropagation()}

          >

            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10 }}>

              <h4 style={{ margin: 0, color: "#0b2f3e" }}>
                {suspendedUser?.accessReason === "TRIAL_EXPIRED"
                  ? "Activa tu membresía"
                  : "Actualizar método de pago"}
              </h4>

              <button

                type="button"

                onClick={() => !payLoading && setOpenPaymentModal(false)}

                style={{

                  width: 36,

                  height: 36,

                  borderRadius: 12,

                  border: "1px solid rgba(0,0,0,.08)",

                  background: "white",

                  cursor: "pointer",

                  display: "grid",

                  placeItems: "center",

                }}

                aria-label="Cerrar"

              >

                <X size={18} />

              </button>

            </div>

            <p style={{ marginTop: 8, fontSize: 12, opacity: 0.75 }}>

              Por seguridad, no almacenamos el número de tu tarjeta.

            </p>

            {/* selector tipo tarjeta */}

            <div style={{ display: "flex", gap: 10, marginTop: 10 }}>

              <button

                type="button"

                onClick={() => setCardType("visa")}

                disabled={payLoading}

                style={{

                  flex: 1,

                  borderRadius: 14,

                  border: cardType === "visa" ? "2px solid #0095A9" : "1px solid rgba(0,0,0,.10)",

                  background: "white",

                  padding: 10,

                  cursor: "pointer",

                }}

                aria-label="Visa"

              >

                <img src={visa} alt="Visa" style={{ height: 18 }} />

              </button>

              <button

                type="button"

                onClick={() => setCardType("mc")}

                disabled={payLoading}

                style={{

                  flex: 1,

                  borderRadius: 14,

                  border: cardType === "mc" ? "2px solid #0095A9" : "1px solid rgba(0,0,0,.10)",

                  background: "white",

                  padding: 10,

                  cursor: "pointer",

                }}

                aria-label="Mastercard"

              >

                <img src={mc} alt="Mastercard" style={{ height: 18 }} />

              </button>

              <button

                type="button"

                onClick={() => setCardType("amex")}

                disabled={payLoading}

                style={{

                  flex: 1,

                  borderRadius: 14,

                  border: cardType === "amex" ? "2px solid #0095A9" : "1px solid rgba(0,0,0,.10)",

                  background: "white",

                  padding: 10,

                  cursor: "pointer",

                }}

                aria-label="American Express"

              >

                <img src={amex} alt="American Express" style={{ height: 18 }} />

              </button>

            </div>

            {/* preview tarjeta */}

            <div

              style={{

                marginTop: 12,

                borderRadius: 18,

                padding: 14,

                background: "linear-gradient(90deg, #30A2A8 0%, #0E6C79 60%, #083F4A 100%)",

                color: "white",

              }}

              aria-hidden="true"

            >

              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>

                <img src={chipImg} alt="" style={{ height: 26, opacity: 0.95 }} />

                <img

                  src={cardType === "visa" ? visa : cardType === "mc" ? mc : amex}

                  alt=""

                  style={{ height: 18 }}

                />

              </div>

              <div style={{ marginTop: 14, fontSize: 18, letterSpacing: 2 }}>

                {cardNumber ? formatCardNumber(cardNumber) : "0000 0000 0000 0000"}

              </div>

              <div

                style={{

                  marginTop: 12,

                  display: "flex",

                  justifyContent: "space-between",

                  gap: 10,

                  fontSize: 12,

                  opacity: 0.95,

                }}

              >

                <div style={{ textTransform: "uppercase" }}>{cardHolder ? cardHolder : "NOMBRE TITULAR"}</div>

                <div>

                  {(cardExpMonth || "MM")}/{(cardExpYear || "YY")}

                </div>

              </div>

            </div>

            {payError && (

              <div

                style={{

                  marginTop: 10,

                  background: "rgba(255,0,0,.08)",

                  border: "1px solid rgba(255,0,0,.15)",

                  padding: 10,

                  borderRadius: 14,

                }}

              >

                ⚠ {payError}

              </div>

            )}

            {paySuccess && (

              <div

                style={{

                  marginTop: 10,

                  background: "rgba(34,197,94,.10)",

                  border: "1px solid rgba(34,197,94,.18)",

                  padding: 10,

                  borderRadius: 14,

                }}

              >

                ✔ Pago aprobado — cuenta reactivada

              </div>

            )}

            <div style={{ marginTop: 12, display: "grid", gap: 10 }}>

              <label style={{ display: "grid", gap: 6, fontSize: 13, color: "#0b2f3e" }}>

                Nombre y apellido del titular

                <input

                  value={cardHolder}

                  onChange={(e) => setCardHolder(normalizeCardHolder(e.target.value))}

                  placeholder="Tu nombre"

                  autoFocus

                  disabled={payLoading}

                  style={{

                    padding: "12px 12px",

                    borderRadius: 14,

                    border: "1px solid rgba(0,0,0,.10)",

                    outline: "none",

                  }}

                />

              </label>

              <label style={{ display: "grid", gap: 6, fontSize: 13, color: "#0b2f3e" }}>

                Número de tarjeta

                <input

                  value={cardNumber}

                  onChange={(e) => setCardNumber(formatCardNumber(e.target.value))}

                  inputMode="numeric"

                  placeholder="4242 4242 4242 4242"

                  maxLength={19}

                  disabled={payLoading}

                  style={{

                    padding: "12px 12px",

                    borderRadius: 14,

                    border: "1px solid rgba(0,0,0,.10)",

                    outline: "none",

                  }}

                />

              </label>

              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 10 }}>

                <label style={{ display: "grid", gap: 6, fontSize: 13, color: "#0b2f3e" }}>

                  Mes

                  <input

                    value={cardExpMonth}

                    onChange={(e) => setCardExpMonth(onlyDigits(e.target.value).slice(0, 2))}

                    inputMode="numeric"

                    placeholder="MM"

                    maxLength={2}

                    disabled={payLoading}

                    style={{

                      padding: "12px 12px",

                      borderRadius: 14,

                      border: "1px solid rgba(0,0,0,.10)",

                      outline: "none",

                    }}

                  />

                </label>

                <label style={{ display: "grid", gap: 6, fontSize: 13, color: "#0b2f3e" }}>

                  Año

                  <input

                    value={cardExpYear}

                    onChange={(e) => setCardExpYear(onlyDigits(e.target.value).slice(0, 2))}

                    inputMode="numeric"

                    placeholder="YY"

                    maxLength={2}

                    disabled={payLoading}

                    style={{

                      padding: "12px 12px",

                      borderRadius: 14,

                      border: "1px solid rgba(0,0,0,.10)",

                      outline: "none",

                    }}

                  />

                </label>

                <label style={{ display: "grid", gap: 6, fontSize: 13, color: "#0b2f3e" }}>

                  CVC

                  <input

                    value={cardCvc}

                    onChange={(e) => setCardCvc(onlyDigits(e.target.value).slice(0, 4))}

                    inputMode="numeric"

                    placeholder="CVV"

                    maxLength={4}

                    disabled={payLoading}

                    style={{

                      padding: "12px 12px",

                      borderRadius: 14,

                      border: "1px solid rgba(0,0,0,.10)",

                      outline: "none",

                    }}

                  />

                </label>

              </div>

              {/* ✅ UN SOLO BOTÓN */}

              <button

                type="button"

                onClick={handleSaveAndChargeFromLogin}

                disabled={payLoading}

                style={{

                  width: "100%",

                  padding: "12px 12px",

                  borderRadius: 14,

                  border: "none",

                  background: "#0095A9",

                  cursor: "pointer",

                  fontWeight: 900,

                  color: "white",

                  marginTop: 6,

                }}

              >

                {payLoading ? "Procesando..." : "Guardar y cobrar"}

              </button>

            </div>

          </div>

        </div>

      )}

    </>

  );

}